import * as queryString from 'query-string';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { LegacyPrimaryButton as PrimaryButton } from '@videoblocks/storywind';

import { ContentClass } from '../../../app/Shared/enums';
import {
  selectIsLoggedIn,
  selectPrimarySubscription,
} from '../../../auth/AuthSelectors';
import Logger from '../../../common/Logger';
import SiteConstants from '../../../common/SiteConstants/SiteConstants';
import { ucfirst } from '../../../common/utils';
import { PAYWALL_DOWNLOAD_BLOCKED_TAG } from '../../constants';
import {
  selectDetailsAcquisitionOption,
  selectDetailsCanDownload,
  selectDetailsSignUpLink,
  selectDetailsStockItem,
} from '../../selectors/detailsSelectors';
import MemberDownloadUpgradeCTA from '../download/cta/MemberDownloadUpgradeCTA';

type DownloadCtaButtonProps = {
  contentClass: ContentClass;
  hasOtherSubscriptions: boolean;
  primaryActionHandler?: () => () => void;
};

const memberDownloadHandler = async (
  stockItem,
  selectedAcquisitionOption,
  primarySubscription,
  dispatch
) => {
  Logger.produceDownloadClickEvent(stockItem);

  const dataBridge = await SiteConstants.buildInstance().getDataBridge();

  dataBridge.downloadItem(
    queryString.stringifyUrl(
      {
        url: selectedAcquisitionOption.downloadUrl,
      },
      {
        skipNull: true,
      }
    ),
    queryString.stringifyUrl(
      {
        url: selectedAcquisitionOption.downloadAjaxUrl,
      },
      {
        skipNull: true,
      }
    ),
    undefined,
    dispatch,
    // NOTE: used to provide data for Intercom in Starter Plan A/B Test, remove after test if possible
    primarySubscription
  );
};

export const buildMemberDownloadHandler = () => {
  const stockItem = useSelector(selectDetailsStockItem);
  const selectedAcquisitionOption = useSelector(selectDetailsAcquisitionOption);
  const primarySubscription = useSelector(selectPrimarySubscription);

  const dispatch = useDispatch();
  return () => {
    Telemetry.increment('download.clicked.details');
    memberDownloadHandler(
      stockItem,
      selectedAcquisitionOption,
      primarySubscription,
      dispatch
    );
  };
};

const MemberDownloadCta = ({
  primaryActionHandler = buildMemberDownloadHandler,
}) => {
  return (
    <>
      <MemberDownloadUpgradeCTA primaryActionHandler={primaryActionHandler()} />
    </>
  );
};

const NonSubscriberDownloadCta = ({
  contentClass,
  hasOtherSubscriptions,
}: DownloadCtaButtonProps) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const signUpLink = useSelector(selectDetailsSignUpLink);
  const cta = hasOtherSubscriptions
    ? 'Add ' + ucfirst(contentClass) + ' Plan to Download this Content'
    : isLoggedIn
    ? 'Subscribe to Download'
    : 'Sign Up to Download';
  return (
    <div
      className="text-center mt-0 mb-3"
      onClick={() => Telemetry.increment(PAYWALL_DOWNLOAD_BLOCKED_TAG)}
      role="presentation"
    >
      <PrimaryButton
        className="hover:no-underline block w-9/10 rounded-lg"
        label={cta}
        href={signUpLink}
      />
    </div>
  );
};

const DownloadCtaButton = ({
  contentClass,
  hasOtherSubscriptions,
  primaryActionHandler,
}: DownloadCtaButtonProps) => {
  const canDownload = useSelector(selectDetailsCanDownload);
  if (canDownload) {
    return <MemberDownloadCta primaryActionHandler={primaryActionHandler} />;
  }
  return (
    <NonSubscriberDownloadCta
      contentClass={contentClass}
      hasOtherSubscriptions={hasOtherSubscriptions}
    />
  );
};

export default DownloadCtaButton;
